import React from "react";
import { Box, Skeleton } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "../useChartTheme";
import type { VisualizationMeta } from "hooks/useNLQuery";

interface Props {
  rows: Record<string, unknown>[];
  columns: string[];
  visualization: VisualizationMeta;
  loading?: boolean;
}

const AgenticLineChart: React.FC<Props> = ({ rows, columns, visualization, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, seriesColors, textPrimary, chartHeight } =
    useChartTheme();

  if (loading || !rows.length) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  const xCol = visualization.x_axis || columns.find((c) => c !== "patient_id") || columns[0];
  const yCol = visualization.y_axis || columns.find((c) => c !== xCol && c !== "patient_id") || columns[1];
  const seriesCol = visualization.series;

  if (!xCol || !yCol) return null;

  const sortedRows = [...rows].sort((a, b) => {
    const va = String(a[xCol] ?? "");
    const vb = String(b[xCol] ?? "");
    return va.localeCompare(vb);
  });

  if (seriesCol) {
    const groups = [...new Set(sortedRows.map((r) => String(r[seriesCol] ?? "")))];
    const categories = [...new Set(sortedRows.map((r) => String(r[xCol] ?? "")))];

    const series = groups.map((group, i) => ({
      name: group,
      type: "line" as const,
      data: categories.map((cat) => {
        const match = sortedRows.find(
          (r) => String(r[xCol]) === cat && String(r[seriesCol]) === group
        );
        return match ? Number(match[yCol] ?? 0) : null;
      }),
      smooth: true,
      symbol: "circle",
      symbolSize: 6,
      lineStyle: { width: 2 },
      color: seriesColors[i % seriesColors.length],
    }));

    const option = {
      tooltip: { ...baseTooltip, trigger: "axis" as const },
      legend: { textStyle: { color: textPrimary, fontSize: 11 }, bottom: 0 },
      grid: { ...baseGrid, bottom: "15%" },
      xAxis: { ...baseAxis, type: "category" as const, data: categories, boundaryGap: false },
      yAxis: { ...baseAxis, type: "value" as const },
      series,
    };

    return (
      <Box sx={{ width: "100%", height: chartHeight }}>
        <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
      </Box>
    );
  }

  const labels = sortedRows.map((r) => String(r[xCol] ?? ""));
  const values = sortedRows.map((r) => Number(r[yCol] ?? 0));

  const option = {
    tooltip: { ...baseTooltip, trigger: "axis" as const },
    grid: baseGrid,
    xAxis: { ...baseAxis, type: "category" as const, data: labels, boundaryGap: false },
    yAxis: { ...baseAxis, type: "value" as const },
    series: [
      {
        type: "line" as const,
        data: values,
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { width: 2, color: seriesColors[0] },
        areaStyle: { color: `${seriesColors[0]}20` },
        itemStyle: { color: seriesColors[0] },
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default AgenticLineChart;
